/**
 * Unified CPSC transform utility.
 *
 * Composes existing utilities to provide a single API for transforming
 * CPSC data between form state and GraphQL schema format.
 *
 * - toSchema: Cleans fields + transforms entities (for form submission)
 * - toForm: Transforms entities for form consumption (for display)
 */
import omit from 'lodash/omit';
import type {
  UsCpscPgaProgram,
  UsCpscPgaProgramInput,
  CpscItem,
} from '__generated__/graphql';
import {
  transformCpscEntities,
  type CpscEntityFormState,
} from './transformEntities';
import { cleanCpscFields } from './cleanCpscFields';

/**
 * Form state type for CpscItem with flat entities[] array (instead of structured arrays)
 */
export type CpscItemFormState = Omit<
  CpscItem,
  'manufacturers' | 'laboratories' | 'certifyingEntities' | 'pointOfContacts'
> & {
  entities?: CpscEntityFormState[];
};

/**
 * Form state type for CPSC program with flat entities[] per item
 */
export type UsCpscFormState = Omit<UsCpscPgaProgram, 'items'> & {
  items?: CpscItemFormState[];
};

// Keys to omit when transforming to schema format
const ENTITY_SCHEMA_OMIT_KEYS = ['entities'] as const;

// Keys to omit when transforming to form format
const ENTITY_FORM_OMIT_KEYS = [
  'manufacturers',
  'laboratories',
  'certifyingEntities',
  'pointOfContacts',
] as const;

/**
 * Unified CPSC transform utility for form ↔ schema transformations.
 *
 * @example
 * // Form submission (form → schema):
 * const schemaData = transformCpsc.toSchema(formData);
 *
 * @example
 * // Load data (schema → form):
 * const formData = transformCpsc.toForm(schemaData);
 */
export const transformCpsc = {
  /**
   * Transform form data to schema format for API submission.
   *
   * @param {Partial<UsCpscFormState>} formData - Form data to transform
   * @return {UsCpscPgaProgramInput} Cleaned and transformed schema data
   */
  toSchema: (formData: Partial<UsCpscFormState>): UsCpscPgaProgramInput => {
    // Step 1: Transform items (entities flat → structured)
    const transformedItems = formData.items?.map((item) => ({
      ...omit(item, ENTITY_SCHEMA_OMIT_KEYS),
      ...transformCpscEntities.toSchema(item.entities ?? []),
    })) as CpscItem[] | undefined;

    // Step 2: Clean fields by processing type
    return cleanCpscFields({
      ...formData,
      items: transformedItems,
    } as UsCpscPgaProgram);
  },

  /**
   * Transform schema data to form format for display.
   *
   * @param {UsCpscPgaProgram} schemaData - Schema data to transform
   * @return {Partial<UsCpscFormState>} Transformed form data
   */
  toForm: (schemaData: UsCpscPgaProgram): Partial<UsCpscFormState> => {
    // Transform items (entities structured → flat)
    const transformedItems = schemaData.items?.map((item) => ({
      ...omit(item, ENTITY_FORM_OMIT_KEYS),
      entities: transformCpscEntities.toForm(item),
    })) as CpscItemFormState[] | undefined;

    return {
      ...omit(schemaData, ['items']),
      items: transformedItems,
    };
  },
};

export default transformCpsc;
